import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, Heading, Image, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';

import cardImage from 'assets/ExistingUserQuestionnaire.png';
import { useLicensedTour } from 'components/shared/LicensedTour';
import { useUnlicensedTour } from 'components/shared/UnlicensedTour';
import useAuthUser from 'hooks/useAuthUser';
import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import QuestionnaireOverlay from './QuestionnaireOverlay';

const QuestionnaireCard = () => {
  const { isAuthenticated } = useAuth();
  const { data: authUser } = useAuthUser();
  const { state: licensedTourState } = useLicensedTour();
  const { state: unlicensedTourState } = useUnlicensedTour();
  const { ver, system_model } = useVersionInfo();
  const location = useLocation();

  const [isHidden, setIsHidden] = useState(false);
  const [isCompletedLocally, setIsCompletedLocally] = useState(false);
  const isCompleted = isCompletedLocally || authUser?.questionnaire_completed === true;
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const hasTrackedShown = useRef(false);

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.200');
  const bodyColor = useColorModeValue('gray.600', 'gray.300');
  const remindColor = useColorModeValue('gray.500', 'gray.400');

  const isHomepage = location.pathname === `/${ver}/${system_model}` || location.pathname === `/${ver}/${system_model}/`;

  const tourCompleted =
    licensedTourState.hasCompletedTour || unlicensedTourState.hasCompletedTour || authUser?.tour_completed === true;
  const tourActive = licensedTourState.isActive || unlicensedTourState.isActive;

  const isVisible = isAuthenticated && !isCompleted && !isHidden && tourCompleted && !tourActive && isHomepage;

  useEffect(() => {
    if (isVisible && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_CARD_SHOWN, { trigger: 'existing_user' });
    }
  }, [isVisible]);

  const handleRemindLater = useCallback(() => {
    mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_CARD_DISMISSED);
    setIsHidden(true);
  }, []);

  const handleStartQuestionnaire = useCallback(() => {
    setIsOverlayOpen(true);
  }, []);

  const handleOverlayClose = useCallback(() => {
    setIsOverlayOpen(false);
  }, []);

  const handleOverlayComplete = useCallback(() => {
    setIsOverlayOpen(false);
    setIsCompletedLocally(true);
  }, []);

  const headingId = 'questionnaire-card-heading';
  const contentId = 'questionnaire-card-content';

  if (!isVisible && !isOverlayOpen) return null;

  return (
    <>
      {isVisible && !isOverlayOpen && (
        <Box
          role="dialog"
          aria-labelledby={headingId}
          aria-describedby={contentId}
          position="fixed"
          right="24px"
          bottom="24px"
          zIndex="popover"
          bg={bgColor}
          borderRadius="lg"
          maxW="320px"
          boxShadow="xl"
          borderWidth="0.5px"
          borderColor={borderColor}
          p={6}
        >
          <Image src={cardImage} alt="Questionnaire illustration" w="100%" borderRadius="md" mb={4} />
          <Stack spacing={3}>
            <Heading id={headingId} size="sm">
              Help us improve ecoQuery
            </Heading>
            <Text id={contentId} fontSize="sm" color={bodyColor}>
              We're working to better understand our users so we can improve your experience. Answer 3 quick questions about
              your role and experience (30 sec).
            </Text>
            <Text
              as="button"
              fontSize="sm"
              color={remindColor}
              onClick={handleRemindLater}
              cursor="pointer"
              textAlign="center"
              _hover={{ textDecoration: 'underline' }}
            >
              Remind me later
            </Text>
            <Button colorScheme="green" size="sm" onClick={handleStartQuestionnaire}>
              Answer 3 questions
            </Button>
          </Stack>
        </Box>
      )}
      <QuestionnaireOverlay
        isOpen={isOverlayOpen}
        onClose={handleOverlayClose}
        onComplete={handleOverlayComplete}
        trigger="existing_user_card"
        allowSkip={false}
      />
    </>
  );
};

export default QuestionnaireCard;
